import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ImageCropperModule } from 'ngx-image-cropper';
import { IPublicClientApplication, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent, ProtectedResourceScopes } from '@azure/msal-angular';
import { NgModule } from '@angular/core';

import { ApiModule, BASE_PATH as CLIENT_BASE_PATH } from 'pcl-hrimageuploader-fa';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthInterceptor } from './utilities/auth.interceptor';
import { BASE_PATH } from 'variables';
import { BaseGuard } from './guards/base.guard';
import { ConfigService } from './services/config.service';
import { GroupGuard } from './guards/group.guard';
import { HomeComponent } from './views/home/home.component';
import { MaterialModule } from './material.module';
import { PhotoUploadingDialog } from './components/dialogs/photo-uploading/photo-uploading-dialog';
import { PhotoUploadSuccessDialog } from './components/dialogs/photo-upload-success/photo-upload-success-dialog';
import { UnauthorizedComponent } from './views/unauthorized/unauthorized.component';
import { UtilitiesService } from './services/utilities.service';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(ConfigService.msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();

    // protectedResourceMap.set(protectedResources.apiTodoList.endpoint, protectedResources.apiTodoList.scopes);

    return {
        interactionType: InteractionType.Popup,
        protectedResourceMap,
    };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: ConfigService.loginRequest
    };
}

export function apiBasePath(configService: ConfigService): string {
    return configService.functionAppBasePath;
}

@NgModule({ declarations: [
        AppComponent,
        HomeComponent,
        UnauthorizedComponent
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        FormsModule,
        MsalModule,
        MsalModule,
        ImageCropperModule,
        PhotoUploadingDialog,
        PhotoUploadSuccessDialog,
        ApiModule], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'fill',
                subscriptSizing: 'dynamic'
            }
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                hasBackdrop: true
            }
        },
        {
            provide: BASE_PATH,
            useValue: '/'
        },
        {
            provide: CLIENT_BASE_PATH,
            deps: [ConfigService],
            useFactory: apiBasePath
        },
        MsalService,
        BaseGuard,
        GroupGuard,
        MsalBroadcastService,
        UtilitiesService,
        ConfigService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
